/**
 * `GET /api/capabilities` (ADR-0008), shared. Fetched once and exposed via
 * context — promoted from three ad-hoc per-screen fetches (AppShell, Login,
 * Webhooks) because SaaS billing (ADR-0019) is the first feature needing the
 * flag in two places at once (the settings nav filter and a route guard);
 * this is the same shape {@link BrandingProvider} already uses for the same
 * kind of "fetch once, read from many places" per-deployment data.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api.js';
import type { ServerCapabilities } from './types.js';

const CapabilitiesContext = createContext<ServerCapabilities | null>(null);

export function CapabilitiesProvider({ children }: { children: ReactNode }): ReactNode {
  const [capabilities, setCapabilities] = useState<ServerCapabilities | null>(null);

  useEffect(() => {
    void api.capabilities().then(setCapabilities);
  }, []);

  return <CapabilitiesContext.Provider value={capabilities}>{children}</CapabilitiesContext.Provider>;
}

/** `null` while the initial fetch is in flight. */
export function useCapabilities(): ServerCapabilities | null {
  return useContext(CapabilitiesContext);
}
